import { usePatchActionBatch } from "@api";
import type { IAction } from "@bbj-types/action";
import type { Override } from "@bbj-types/utils";
import { AxiosError } from "axios";
import { useMemo } from "react";

type TUpdateActionList = Pick<IAction, "_id" | "status" | "executionDate">;
export interface IUseValidateActionButton {
  tooltipTitle: string | null;
  disableButton: boolean;
  updateActionList: <T extends TUpdateActionList>(actions: T[], executionDate: Date) => Promise<T[]>;
  loading: boolean;
}

export type TActionValidateButton = Partial<
  Override<Pick<IAction, "type" | "jobSituation" | "apply">, { apply?: unknown }>
>;

export const actionHasRequiredFields = (action: TActionValidateButton) => {
  if (action.type === "job-follow-up") {
    return !!action.jobSituation && !!action.apply;
  }
  return true;
};

export const useValidateActionButton = (action: TActionValidateButton): IUseValidateActionButton => {
  const [{ loading }, patchActionBatch] = usePatchActionBatch();

  const disableButton = useMemo(() => {
    return !actionHasRequiredFields(action);
  }, [action]);

  const tooltipTitle = useMemo(() => {
    return disableButton
      ? "Vous ne pouvez pas valider l’action tant que tous les champs obligatoires de l’action ne sont pas renseignés. Cliquez sur modifier pour les remplir."
      : null;
  }, [disableButton]);

  const updateActionList = async <T extends TUpdateActionList>(actions: T[], executionDate: Date): Promise<T[]> => {
    const updatedFields: Omit<TUpdateActionList, "_id"> = {
      status: "done",
      executionDate: executionDate,
    };

    const actionsToUpdate = actions.map((a) => ({ _id: a._id, ...updatedFields }));

    try {
      await patchActionBatch(actionsToUpdate);
      window.publish("feedback", {
        severity: "success",
        message:
          actions.length > 1
            ? "Les actions ont bien été indiquées comme réalisées."
            : "L’action a bien été indiquée comme réalisée.",
      });

      return actions.map<T>((a) => ({ ...a, ...updatedFields }));
    } catch (error) {
      window.publish("feedback", {
        severity: "error",
        message:
          error instanceof AxiosError || actions.length > 1
            ? "Une erreur est survenue lors de la mise à jour des actions. Veuillez contacter un administrateur."
            : "Une erreur est survenue lors de la mise à jour de l'action. Veuillez contacter un administrateur.",
      });
      return [];
    }
  };

  return { tooltipTitle, disableButton, loading, updateActionList };
};
